import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { app as routes } from '@configs/routes.json';

import {
  translateStatus,
  getHeadings,
  OrderListSort,
  OrderWithFormats,
} from '../common';
import {
  StageUploaded,
  StageProcessing,
  StageFinished,
} from '../common-components';
import './OrdersList.scss';

export interface IOrdersListStateProps {
  sortType: OrderListSort;
  sortDescending: boolean;
  sortedOrders: OrderWithFormats[];
}

export interface IOrdersListDispatchProps {
  sort: (sortType: OrderListSort) => void;
}

const OrdersList: React.FC<IOrdersListStateProps & IOrdersListDispatchProps> =
  ({ sortType, sortDescending, sortedOrders, sort }) => {
    const { t } = useTranslation();

    const headings = getHeadings(t);

    const onSortHeading = (sortType?: OrderListSort): void => {
      if (sortType) {
        sort(sortType);
      }
    };

    const headingClasses = (headingSortType?: OrderListSort): string => {
      const classes: string[] = [];
      classes.push(headingSortType === sortType ? 'active' : 'not-active');
      if (!headingSortType) {
        classes.push('static');
      }
      return classes.join(' ');
    };

    return (
      <React.Fragment>
        <table
          className={`standard-table orders-list ${
            sortDescending ? 'desc' : 'asc'
          }`}
        >
          <thead>
            <tr>
              {headings.map((heading, index) => (
                <th
                  className={headingClasses(heading.sortType)}
                  onClick={() => onSortHeading(heading.sortType)}
                  key={index}
                  colSpan={heading.colSpan ?? 1}
                >
                  {heading.text}
                  <div
                    className='ml-1 fcolor--primary-brand d-inline-block sortable-icon'
                    style={{ width: 8 }}
                  >
                    <i className='fas fa-long-arrow-alt-down'></i>
                    <i className='fas fa-long-arrow-alt-up'></i>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedOrders.map((order, index) => {
              const getLinkToOrderDetailsPage = (children: React.ReactNode) => (
                <Link to={`${routes.orders}/${order.orderNumber}`}>
                  {children}
                </Link>
              );
              return (
                <tr key={index}>
                  <td className='text-uppercase'>
                    {getLinkToOrderDetailsPage(
                      translateStatus(t, order.status)
                    )}
                  </td>
                  <td>{getLinkToOrderDetailsPage(order.orderNumber)}</td>
                  <td>{getLinkToOrderDetailsPage(order.description)}</td>

                  <td className='border-right-0'>
                    {getLinkToOrderDetailsPage(
                      <StageUploaded
                        className='clickable-sort-icon'
                        count={order.sampleSummary.uploaded}
                      />
                    )}
                  </td>

                  <td
                    className={
                      order.sampleSummary.isProcessingWithWarning
                        ? 'orders-stage-warning'
                        : ''
                    }
                  >
                    {getLinkToOrderDetailsPage(
                      <StageProcessing
                        className='clickable-sort-icon'
                        count={order.sampleSummary.processing}
                        anyPassedProcessing={order.sampleSummary.finished > 0}
                      />
                    )}
                  </td>
                  <td>
                    <StageFinished count={order.sampleSummary.finished} />
                  </td>

                  <td>{order.sampleSummary.latestProcessedFormatted}</td>

                  <td>{order.createdFormatted}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </React.Fragment>
    );
  };

export default OrdersList;
